import { LOB_IDS } from "@/shared/common/static";
import type {
  CarAdditionalInfoValues,
  CarDriverFormValues,
  CarPersonalFormValues,
  CarUploadedDocumentMap,
  CarVehicleFormValues,
} from "../redux/type";
import { normalizeCurrencyInput } from "./motorSumInsured";

function buildSelectedOptionalCovers(selectedAddOns: string[] = []) {
  return selectedAddOns
    .map((label) => label?.trim())
    .filter((label): label is string => Boolean(label))
    .map((label) => ({
      key: label,
      value: "true",
    }));
}

function normalizeNullableNumber(value: string | number | null | undefined) {
  const normalizedValue = value === null || value === undefined ? null : Number(value);

  return normalizedValue === null || Number.isNaN(normalizedValue) ? null : normalizedValue;
}

function buildCarPlanName(selectedQuote: any) {
  const insurerPrefix = selectedQuote?.insurer?.name
    ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
    .trim();

  return insurerPrefix
    ? `${insurerPrefix}-${selectedQuote?.plan?.name}`
    : selectedQuote?.plan?.name;
}

function toCarAttachType(fieldKey: string) {
  return fieldKey
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

function buildCarUploadDocAttachmentsPayload(documents: CarUploadedDocumentMap) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>[]>(
    (attachments, [fieldKey, document]) => {
      if (!document) {
        return attachments;
      }

      attachments.push({
        docName: document.fileName || document.filename || "uploaded_document",
        docType: document.mimeType || "image/jpeg",
        file:
          (document as any).file || (document as any).base64 || (document as any).fileData || "",
        attachType: toCarAttachType(fieldKey),
      });

      return attachments;
    },
    []
  );
}

function normalizeMotorGender(gender: CarDriverFormValues["gender"]) {
  if (gender === "male") return "Male";
  if (gender === "female") return "Female";

  return "";
}

function buildCarTravellersMoreInfo(params: {
  personalDetails: CarPersonalFormValues;
  driverDetails: CarDriverFormValues;
  additionalInfo: CarAdditionalInfoValues;
}) {
  const { personalDetails, driverDetails, additionalInfo } = params;

  return [
    {
      first_name: personalDetails.firstName || "",
      last_name: personalDetails.lastName || "",
      date_of_birth: driverDetails.dateOfBirth || "",
      gender: normalizeMotorGender(driverDetails.gender),
      nationality: driverDetails.nationality || "",
      emiratesId: additionalInfo.emiratesId || "",
      estimatedAnnualMileage: additionalInfo.estimatedAnnualMileage || "",
      chassisNumber: additionalInfo.chassisNumber || "",
      carFinancedBy: additionalInfo.carFinancedBy || "",
      vehicleColor: additionalInfo.vehicleColor || "",
      tcfNumber: additionalInfo.tcfNumber || "",
      registrationType: additionalInfo.registrationType || "",
      plateCode: additionalInfo.plateCode || "",
      plateNumber: additionalInfo.plateNumber || "",
      engineNumber: additionalInfo.engineNumber || "",
      customerAuthorizationAccepted: additionalInfo.authorizationAccepted || false,
    },
  ];
}

export function buildCarCreateSessionPayload(personalData: CarPersonalFormValues) {
  const cleanCountryCode = personalData.mobileNumber.countryCode.replace(/\D/g, "");

  return {
    assigned_user_id: null,
    payload: {
      first_name: personalData.firstName,
      last_name: personalData.lastName || "",
      country_code: cleanCountryCode,
      mobile_number: personalData.mobileNumber.number,
      email: personalData.email,
      partner_code: "",
      friends_and_family_contact: "",
      marketing_consent: "yes",
    },
    lob_id: LOB_IDS.MOTOR,
  };
}

export function buildCarPersonalDetailsPayload(personalData: CarPersonalFormValues) {
  return {
    first_name: personalData.firstName,
    last_name: personalData.lastName || "",
    mobile_country_code: `+${personalData.mobileNumber.countryCode.replace(/\D/g, "")}`,
    mobile_number: personalData.mobileNumber.number,
    email: personalData.email,
    marketing_consent: personalData.marketingConsent,
  };
}

export function buildCarVehicleDetailsPayload(vehicleData: CarVehicleFormValues) {
  return {
    car_model: vehicleData.carModel,
    model_year: vehicleData.modelYear,
    vehicle_variant: vehicleData.vehicleVariant,
    gcc_spec: vehicleData.gccSpec,
    is_new_car: vehicleData.isNewCar,
    registered_emirate: vehicleData.registeredEmirate,
    registration_date: vehicleData.registrationDate,
    registered_under: vehicleData.registeredUnder,
    car_value: normalizeCurrencyInput(vehicleData.carValue),
  };
}

export function buildCarDriverDetailsPayload(driverData: CarDriverFormValues) {
  const hasClaimsLastYear = driverData.claimsLastYear === true;

  return {
    date_of_birth: driverData.dateOfBirth,
    nationality: driverData.nationality,
    gender: driverData.gender,
    uae_driving_experience: driverData.uaeDrivingExperience,
    outside_uae_driving_experience: driverData.outsideUaeDrivingExperience,
    has_active_policy: driverData.hasActivePolicy,
    current_policy_type: driverData.currentPolicyType,
    current_insurer: driverData.currentInsurer,
    claims_last_year: driverData.claimsLastYear,
    ...(!hasClaimsLastYear ? { no_claims_certificate: driverData.no_claims_certificate } : {}),
  };
}

export function buildCarAttachmentsPayload(documents: CarUploadedDocumentMap) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>>(
    (accumulator, [fieldKey, document]) => {
      if (!document) {
        return accumulator;
      }

      accumulator[fieldKey] = {
        docName: document.fileName || document.filename,
        docType: document.mimeType || "",
        doc_url:
          (document.uploadResponse as { payload?: { final_filename?: string } } | undefined)
            ?.payload?.final_filename || "",
        size: document.size || 0,
        uploadedAt: document.uploadedAt,
      };

      return accumulator;
    },
    {}
  );
}

export function buildCarAdditionalInfoPayload(additionalInfo: CarAdditionalInfoValues) {
  return {
    additionalPersonalDetails: {
      gender: "",
      beneficiaryName: "",
      beneficiaryRelationship: "",
      address: "",
      nationality: "",
      sourceOfFunds: "",
      cityOfResidence: "",
      estimatedAnnualMileage: additionalInfo.estimatedAnnualMileage,
      chassisNumber: additionalInfo.chassisNumber,
      carFinancedBy: additionalInfo.carFinancedBy,
      vehicleColor: additionalInfo.vehicleColor,
      tcfNumber: additionalInfo.tcfNumber,
      registrationType: additionalInfo.registrationType,
      plateCode: additionalInfo.plateCode,
      plateNumber: additionalInfo.plateNumber,
      engineNumber: additionalInfo.engineNumber,
    },
    identity: {
      emiratesId: additionalInfo.emiratesId,
      emiratesIdExpiryDate: "",
      emiratesIdIssueDate: "",
      passportNo: "",
      passportExpiryDate: "",
      passportIssueDate: "",
    },
    declarations: {
      isUaeResidentConfirmed: false,
      customerAuthorizationAccepted: additionalInfo.authorizationAccepted,
    },
  };
}

export function buildCarBindPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  selectedAddOns: string[];
  personalDetails: CarPersonalFormValues;
  driverDetails: CarDriverFormValues;
  additionalInfo: CarAdditionalInfoValues;
  lob_id?: number;
}) {
  const {
    selectedQuote,
    clientId,
    sessionId,
    selectedAddOns,
    personalDetails,
    driverDetails,
    additionalInfo,
    lob_id,
  } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: lob_id ?? LOB_IDS.MOTOR,
    plan_name: buildCarPlanName(selectedQuote),
    insurerId: String(selectedQuote?.insurer?.insurer_id ?? ""),
    action: "NEXT_BUTTON",
    insurerPlan: {
      id: String(selectedQuote?.plan?.id ?? ""),
      planName: selectedQuote?.plan?.name || "",
    },
    selectedOptionalCovers: buildSelectedOptionalCovers(selectedAddOns),
    travellers_more_info: buildCarTravellersMoreInfo({
      personalDetails,
      driverDetails,
      additionalInfo,
    }),
    ...buildCarAdditionalInfoPayload(additionalInfo),
    attachments: [],
  };
}

export function buildCarUploadDocPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  selectedAddOns: string[];
  personalDetails: CarPersonalFormValues;
  driverDetails: CarDriverFormValues;
  additionalInfo: CarAdditionalInfoValues;
  documents: CarUploadedDocumentMap;
  lob_id?: number;
}) {
  const {
    selectedQuote,
    clientId,
    sessionId,
    selectedAddOns,
    personalDetails,
    driverDetails,
    additionalInfo,
    documents,
    lob_id,
  } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: lob_id ?? LOB_IDS.MOTOR,
    plan_name: buildCarPlanName(selectedQuote),
    insurerId: String(selectedQuote?.insurer?.insurer_id ?? ""),
    action: "UPLOAD_DOC",
    insurerPlan: {
      id: String(selectedQuote?.plan?.id ?? ""),
      planName: selectedQuote?.plan?.name || "",
    },
    selectedOptionalCovers: buildSelectedOptionalCovers(selectedAddOns),
    travellers_more_info: buildCarTravellersMoreInfo({
      personalDetails,
      driverDetails,
      additionalInfo,
    }),
    ...buildCarAdditionalInfoPayload(additionalInfo),
    attachments: buildCarUploadDocAttachmentsPayload(documents),
  };
}

export function buildCarBuyNowBindPayload(params: {
  quote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  selectedAddOns: string[];
  personalDetails: CarPersonalFormValues;
  driverDetails: CarDriverFormValues;
  additionalInfo: CarAdditionalInfoValues;
  lob_id?: number;
}) {
  const {
    quote,
    clientId,
    sessionId,
    selectedAddOns,
    personalDetails,
    driverDetails,
    additionalInfo,
    lob_id,
  } = params;
  const insurerPrefix = quote?.insurer?.name
    ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
    .trim();

  return {
    client_id: clientId ? Number(clientId) : null,
    session_id: sessionId ? Number(sessionId) : null,
    lob_id: lob_id ?? LOB_IDS.MOTOR,
    plan_name: insurerPrefix ? `${insurerPrefix}-${quote?.plan?.name}` : quote?.plan?.name,
    insurerId: String(quote?.insurer?.insurer_id ?? ""),
    action: "BUY",
    insurerPlan: {
      id: String(quote?.plan?.id ?? ""),
      planName: quote?.plan?.name || "",
    },
    selectedOptionalCovers: buildSelectedOptionalCovers(selectedAddOns),
    travellers_more_info: buildCarTravellersMoreInfo({
      personalDetails,
      driverDetails,
      additionalInfo,
    }),
    additionalPersonalDetails: {
      gender: "",
      beneficiaryName: "",
      beneficiaryRelationship: "",
      address: "",
      nationality: "",
      sourceOfFunds: "",
      cityOfResidence: "",
      estimatedAnnualMileage: "",
      chassisNumber: "",
      carFinancedBy: "",
      vehicleColor: "",
      tcfNumber: "",
      registrationType: "",
      plateCode: "",
      plateNumber: "",
      engineNumber: "",
    },
    identity: {
      emiratesId: "",
      emiratesIdExpiryDate: "",
      emiratesIdIssueDate: "",
      passportNo: "",
      passportExpiryDate: "",
      passportIssueDate: "",
    },
    declarations: {
      isUaeResidentConfirmed: false,
      customerAuthorizationAccepted: false,
    },
    attachments: [],
  };
}

export function buildCarSelectedQuotePayload(quote: any) {
  const commission = quote?.plan?.commission;

  return {
    insurer_name: quote?.insurer?.name || "",
    insurer_id: quote?.insurer?.insurer_id || quote?.insurer?.id || null,
    insurer_logo: quote?.insurer?.insurer_logo || null,
    plan_name: quote?.plan?.name || "",
    plan_id: quote?.plan?.id || null,
    premium: quote?.plan?.price?.amount ?? null,
    vat: quote?.plan?.price?.vat ?? null,
    total_premium: quote?.plan?.price?.amount_with_vat ?? null,
    currency: quote?.plan?.price?.currency || null,
    sections: quote?.sections || [],
    selected_addOns:
      quote?.sections
        ?.find((section: any) => section?.title === "Add-Ons")
        ?.items?.filter((item: any) => item?.is_checked === true)
        ?.map((item: any) => item?.label) || [],
    ...(commission
      ? {
          commission: {
            percentage: commission?.percentage ?? null,
            amount: commission?.amount ?? null,
            currency: commission?.currency || null,
          },
        }
      : {}),
  };
}
